import React from 'react';
import { Shield } from 'lucide-react';
import Header from './components/Header22';
import Footer from './components/Footer22';

const PrivacyPolicy22 = () => {
    const sections = [
        {
            id: 'information-we-collect',
            title: '1. Information We Collect',
            paragraphs: [
                'We collect personal information that you voluntarily provide to us when you register on our website, place an order, subscribe to our newsletter, or contact our customer support team. This information may include your full name, email address, phone number, shipping and billing addresses, date of birth, and body measurements for custom-sizing purposes.',
                'When you make a purchase, your payment information (such as credit card number, UPI ID, or net banking details) is collected and processed directly by our payment gateway partner, Razorpay. We do not store your complete payment card details on our servers. Razorpay is PCI-DSS compliant and adheres to the highest standards of payment security.',
                'We also automatically collect certain technical information when you visit our website, including your IP address, browser type and version, device type, operating system, referring URL, pages viewed, time spent on pages, and clickstream data. This information is collected through cookies and similar tracking technologies to help us improve your browsing experience.'
            ]
        },
        {
            id: 'how-we-use-information',
            title: '2. How We Use Your Information',
            paragraphs: [
                'We use the information we collect to process and fulfil your orders, manage your account, communicate with you about your purchases and order status, and provide customer support. Your contact details may be used to send transactional notifications such as order confirmations, shipping updates via Shiprocket tracking, and delivery confirmations.',
                'With your explicit consent, we may use your email address and phone number to send you marketing communications about new collections, artisan stories, exclusive offers, seasonal sales, and craft trail events. You can opt out of marketing communications at any time by clicking the unsubscribe link in any email or by updating your communication preferences in your account settings.',
                'We use aggregated and anonymised data to analyse shopping trends, improve our product offerings, optimise website performance, personalise your browsing experience, and conduct internal business analytics. This data helps us understand what our customers love and enables us to serve you better.'
            ]
        },
        {
            id: 'data-security',
            title: '3. Data Security',
            paragraphs: [
                'We implement robust technical and organisational security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. Our website uses SSL/TLS encryption to secure all data transmitted between your browser and our servers. All payment transactions are processed through Razorpay\'s secure, PCI-DSS Level 1 certified infrastructure.',
                'Access to personal data is restricted to authorised personnel only, on a need-to-know basis. We conduct regular security audits and vulnerability assessments of our systems. Our hosting infrastructure employs firewalls, intrusion detection systems, and regular backups to ensure data integrity and availability.',
                'While we take every reasonable precaution to protect your information, no method of electronic transmission or storage is completely secure. We cannot guarantee absolute security, but we are committed to promptly notifying affected users in the event of any data breach, in accordance with applicable Indian data protection regulations.'
            ]
        },
        {
            id: 'cookies-tracking',
            title: '4. Cookies & Tracking',
            paragraphs: [
                'Our website uses cookies and similar tracking technologies to enhance your browsing experience, remember your preferences, and gather analytical data. Essential cookies are required for the basic functioning of our website, including maintaining your shopping cart and processing checkout. These cannot be disabled.',
                'We use Google Analytics to collect anonymised data about website traffic and user behaviour. This helps us understand how visitors interact with our website, which pages are most popular, and where improvements are needed. Google Analytics uses cookies to collect this information, but it does not identify individual users. You can opt out of Google Analytics tracking by installing the Google Analytics Opt-out Browser Add-on.',
                'We may also use cookies from social media platforms (such as Instagram and Facebook) to enable social sharing features and to display targeted advertisements on those platforms. You can manage your cookie preferences through your browser settings. Please note that disabling certain cookies may affect the functionality of our website.'
            ]
        },
        {
            id: 'third-party-services',
            title: '5. Third-Party Services',
            paragraphs: [
                'We work with trusted third-party service providers to operate our business effectively. These partners include: Razorpay for secure payment processing, Shiprocket for order fulfilment and shipping logistics, Google Analytics for website analytics, and cloud hosting providers for data storage and website infrastructure.',
                'Each of these service providers is contractually obligated to protect your personal information and may only use it for the specific purposes for which it was shared. We do not sell, trade, or rent your personal information to any third parties for their marketing purposes.',
                'Our website may contain links to third-party websites, including social media platforms and partner artisan cooperatives. We are not responsible for the privacy practices, content, or security of these external websites. We encourage you to read the privacy policies of any third-party website you visit through links on our platform.'
            ]
        },
        {
            id: 'artisan-data',
            title: '6. Artisan Data Protection',
            paragraphs: [
                'We collect and display information about our artisan partners, including their names, photographs, location, and craft speciality, with their explicit consent. This information is shared to provide transparency about the maker behind each product and to celebrate their craft heritage.',
                'Artisans retain the right to request removal or modification of their personal information from our platform at any time. We handle artisan data with the same level of care and security as customer data, ensuring compliance with all applicable data protection laws.',
                'We do not share artisan personal contact information with customers or third parties. All communications between customers and artisans are facilitated through our platform to protect the privacy of both parties.'
            ]
        },
        {
            id: 'your-rights',
            title: '7. Your Rights',
            paragraphs: [
                'You have the right to access, correct, update, or request deletion of your personal information at any time. You can update most of your account information directly through your account settings. For deletion requests or other data-related queries, please contact our privacy team at privacy@maati.in.',
                'You have the right to opt out of marketing communications at any time. You can also request a copy of all personal data we hold about you. We will respond to all data access requests within 30 days of receiving the request, in compliance with applicable Indian data protection regulations.',
                'If you believe that your data has been mishandled or if you wish to file a complaint, you may contact us directly or approach the relevant data protection authority in India. We take all complaints seriously and will investigate and respond promptly.'
            ]
        },
        {
            id: 'contact-us',
            title: '8. Contact Us',
            paragraphs: [
                'If you have any questions, concerns, or requests regarding this Privacy Policy or our data handling practices, please contact us at: privacy@maati.in or through our Contact page. Our Data Protection Officer can be reached at dpo@maati.in.',
                'We may update this Privacy Policy from time to time to reflect changes in our practices, technology, legal requirements, or other factors. We will notify you of any material changes by posting the updated policy on this page and updating the "Last Updated" date. We encourage you to review this page periodically.',
                'This Privacy Policy is effective as of March 2026 and was last updated on March 19, 2026.'
            ]
        },
    ];

    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Cormorant+Infant:ital,wght@0,300;0,400;0,500;0,600;0,700;1,400&family=Source+Sans+3:wght@300;400;500;600;700&display=swap');
            `}</style>

            <div style={{ minHeight: '100vh', backgroundColor: '#FAF6F1', fontFamily: "'Source Sans 3', -apple-system, sans-serif" }}>
                <Header />

                {/* Hero */}
                <section style={{
                    padding: '120px 24px 60px', borderBottom: '1px solid #E8DDD0',
                    textAlign: 'center',
                }}>
                    <div style={{ maxWidth: '700px', margin: '0 auto' }}>
                        <div style={{
                            width: '56px', height: '56px', borderRadius: '50%',
                            backgroundColor: '#F3ECE4', display: 'flex',
                            alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px',
                        }}>
                            <Shield size={24} color="#C4572A" />
                        </div>
                        <h1 style={{
                            fontFamily: "'Cormorant Infant', Georgia, serif",
                            fontSize: 'clamp(36px, 5vw, 52px)', fontWeight: 400,
                            color: '#5C3D2E', marginBottom: '12px',
                        }}>
                            Privacy Policy
                        </h1>
                        <p style={{ fontSize: '14px', color: '#8B7355' }}>
                            Last updated: March 2026
                        </p>
                    </div>
                </section>

                {/* Content */}
                <div style={{ maxWidth: '800px', margin: '0 auto', padding: '60px 24px 80px' }}>
                    {/* Introduction */}
                    <div style={{
                        backgroundColor: '#F3ECE4', borderRadius: '12px', padding: '24px 28px',
                        marginBottom: '48px', borderLeft: '3px solid #C4572A',
                    }}>
                        <p style={{ fontSize: '15px', color: '#5C3D2E', lineHeight: 1.7, margin: 0 }}>
                            At MAATI, we respect your privacy and are committed to protecting your personal information. This Privacy Policy explains how we collect, use, store, and protect your data when you visit our website or make a purchase. We believe in transparency and want you to feel confident when shopping with us.
                        </p>
                    </div>

                    {/* Table of Contents */}
                    <div style={{
                        backgroundColor: '#FFFFFF', borderRadius: '12px',
                        border: '1px solid #E8DDD0', padding: '24px 28px',
                        marginBottom: '48px',
                    }}>
                        <h3 style={{ fontSize: '16px', fontWeight: 600, color: '#5C3D2E', margin: '0 0 16px 0' }}>
                            Table of Contents
                        </h3>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            {sections.map(section => (
                                <a
                                    key={section.id}
                                    href={`#${section.id}`}
                                    style={{
                                        fontSize: '14px', color: '#C4572A', textDecoration: 'none',
                                        transition: 'color 0.2s',
                                    }}
                                    onMouseEnter={(e) => { e.currentTarget.style.color = '#D4683B'; }}
                                    onMouseLeave={(e) => { e.currentTarget.style.color = '#C4572A'; }}
                                >
                                    {section.title}
                                </a>
                            ))}
                        </div>
                    </div>

                    {/* Sections */}
                    {sections.map((section, i) => (
                        <div key={section.id} id={section.id} style={{ marginBottom: i < sections.length - 1 ? '48px' : '0' }}>
                            <h2 style={{
                                fontFamily: "'Cormorant Infant', Georgia, serif",
                                fontSize: '26px', fontWeight: 600, color: '#5C3D2E',
                                marginBottom: '20px', paddingBottom: '12px',
                                borderBottom: '1px solid #E8DDD0',
                            }}>
                                {section.title}
                            </h2>
                            {section.paragraphs.map((para, j) => (
                                <p key={j} style={{
                                    fontSize: '15px', color: '#5C3D2E', lineHeight: 1.8,
                                    marginBottom: j < section.paragraphs.length - 1 ? '16px' : '0',
                                    opacity: 0.85,
                                }}>
                                    {para}
                                </p>
                            ))}
                        </div>
                    ))}
                </div>

                <Footer />
            </div>
        </>
    );
};

export default PrivacyPolicy22;
